import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Buildings, Eye, EyeSlash } from '@phosphor-icons/react';

function formatError(detail) {
  if (detail == null) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan');
  const preselectedInterval = searchParams.get('interval');
  const [form, setForm] = useState({ name: '', email: '', password: '', company: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name, form.company);
      if (preselectedPlan) {
        navigate(`/billing?plan=${preselectedPlan}${preselectedInterval ? `&interval=${preselectedInterval}` : ''}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="w-8 h-8 bg-[#0052FF] rounded-sm flex items-center justify-center">
            <Buildings weight="bold" className="text-white" size={18} />
          </div>
          <span className="text-lg font-black tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>HostCockpit</span>
        </div>
        <div className="bg-white border border-zinc-200 p-8">
          <h1 className="text-2xl font-bold text-zinc-950 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Create your account</h1>
          <p className="text-sm text-zinc-500 mb-6" style={{ fontFamily: 'var(--font-body)' }}>Start managing properties with AI</p>
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm" data-testid="register-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Full Name</label>
              <input type="text" value={form.name} onChange={e => set('name', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] focus:ring-offset-1 rounded-sm" placeholder="John Smith" data-testid="register-name" />
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Email</label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] focus:ring-offset-1 rounded-sm" placeholder="you@email.com" data-testid="register-email" />
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Password</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] focus:ring-offset-1 rounded-sm pr-10" placeholder="Min. 6 characters" data-testid="register-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                  {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Company <span className="text-zinc-400 font-normal">(Optional)</span></label>
              <input type="text" value={form.company} onChange={e => set('company', e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] focus:ring-offset-1 rounded-sm" placeholder="Your company" data-testid="register-company" />
            </div>
            <button type="submit" disabled={loading} className="w-full py-3 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] transition-colors disabled:opacity-50" data-testid="register-submit">
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-zinc-500">Already have an account? <Link to="/login" className="text-[#0052FF] font-bold hover:underline" data-testid="register-login-link">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
